// client.ts — Client-side game logic, rendering, and real-time interpolation

// Import UI types to ensure proper UIBridge type is available
import "./ui/ui.types";
import { gameActions } from "@game/state";
// better-auth removed — stub auth functions until SpacetimeDB auth is wired up
const betterAuthSignOut = async () => {};
const signInWithDiscord = async () => {};
import { runAuthBootstrap } from "./auth/bootstrap";
import { setBugReportDependencies, setupErrorCapture } from "./bug/bug";
import { initializeBugReportUI, showBugReportModal } from "./ui/bug.ui";
import { initializeConnectionStatus } from "./ui/connection.ui";
import { initializeCountdown } from "./ui/countdown.ui";
import {
  initializePerformanceStats,
  updatePerformanceStats as updatePerformanceStatsUI,
} from "./ui/perfstats.ui";
import { initializeSplashText } from "./ui/splash-text.client";

declare global {
  interface Window {
    React?: any;
    ReactDOM?: any;
  }
}

import {
  ASTEROID_SIZES,
  COLORS,
  FONT_FAMILY_CANVAS,
  FONT_FAMILY_CSS,
  FONT_FAMILY_MONOSPACE,
  FRICTION,
  MOUSE_MAX_ROTATION_PER_TICK,
  PLAYER_BASE_SPEED_PER_TICK,
  PLAYER_BOOST_MULTIPLIER,
  PLAYER_THRUST_PER_TICK,
  ROTATION_SNAP_THRESHOLD_TICKS,
  STRAFE_THRUST_MULTIPLIER,
  WORLD,
} from "@game/config";
// Strafe lock handled via input bridge; no local strafe state
import type { BulletInterpolation } from "@game/domain";
import { getThemeMusic, initializeAudioSystem } from "./audio/audio";
import { GlobalOverlay } from "./effects/global-overlay.webgl";
import { GPUExplosionParticles } from "./effects/explosion.particles.webgl";
import {
  createThrustTrail,
  renderThrustTrail,
  type ThrustTrail,
  updateThrustTrail,
} from "./effects/particles";
import { UnifiedPostProcessEffects } from "./effects/postprocess-unified.webgl";

/**
 * Initialize CSS font variables from centralized config
 * This allows runtime updates to fonts across the entire application
 */
function initializeFontCSS() {
  const root = document.documentElement;
  root.style.setProperty("--font-family", FONT_FAMILY_CSS);
  root.style.setProperty(
    "--font-family-monospace",
    `'${FONT_FAMILY_MONOSPACE}', 'Courier New', 'Liberation Mono', 'Consolas', monospace`,
  );
}

import type { Vec2 } from "@game/core";
import { computeTickMovement, computeTickRotation } from "@game/core";
import type { Mine } from "@game/domain";
import type {
  Asteroid,
  Bullet,
  Pellet,
  PerformanceMetrics,
} from "@game/protocol";
import {
  CoordinateValidator,
  setCoordinateValidatorDependencies,
} from "./effects/damage-numbers/coords";
import {
  DamageNumberManager,
  setDamageNumberDependencies,
} from "./effects/damage-numbers/manager";
import {
  setBulletInterpolationState,
  updateBulletPositions,
} from "./game/bullets.interpolate";
import {
  clientGameOrchestrator,
  initializeReactIntegration,
} from "./game/game";
import {
  interpolatePlayers,
  setPlayerInterpolationState,
  setPredictOwnPlayerFunction,
} from "./game/interpolate.players";
import {
  normalizeRotation,
  smoothPosition,
  smoothRotation,
  wrapCoordinate,
} from "./game/smoothing";
import { generateStars, renderStars, type Star } from "./game/stars";
import { detectDeviceType, createInputProvider } from "./input/InputManager";
import type { InputProvider } from "@game/domain";
import {
  getInputBuffer,
  removeAcknowledgedInputs,
  setInputProvider,
  setRealtimeInputDependencies,
  startInputTransmission,
  updateInput,
  setLockedThrustAngle,
} from "./input/input.bridge";
import {
  handleQuickJoin,
  initLobbyBrowserUI,
  joinSpecificLobby,
  requestLobbyList,
  resetDisconnectTracking,
  setBrowserDependencies,
  trackConnectionState,
} from "./lobby/browser";
import {
  startLobbySimulationUpdates,
  stopLobbySimulationUpdates,
} from "./lobby/simulation";
import { initMetricsUI } from "./metrics/dashboard";
import { renderMines } from "./mines/mines";
import { dispatchNetworkMessage } from "./networking/network.dispatch";
import { resolveStdbRuntimeConfig } from "./networking/realtime.config";
import { mapRealtimeEventToLegacyMessages } from "./networking/realtime.events";
import {
  isActiveRealtimeBackendConnected,
  setActiveRealtimeBackend,
} from "./networking/realtime.runtime";
import { createStdbRealtimeBackendFromBindings } from "./networking/stdb.backend";
import {
  dispatchLocalMessage,
  registerMessageHandler,
  setupConsoleRedirection,
} from "./networking/websocket";
import {
  registerAllMessageHandlers,
  setHandlerDependencies,
} from "./networking/ws.handlers";
import type { RealtimeBackend } from "./networking/realtime.types";
import { renderBulletsWithTrails } from "./render/bullets";
import { drawRankInsignia } from "./render/insignia";
import {
  handleJoinNewGame,
  initializeModals,
  setModalDependencies,
  showJoinModal,
} from "./ui/modals";
import { showToast } from "./ui/toasts";

// ============================================================================
// ============================================================================

// Client-side player representation for interpolation
interface ClientPlayer {
  id: string;
  name: string;
  pos: Vec2;
  vel: Vec2;
  rotation: number;
  targetRotation: number;
  angularVelocity: number;
  health: number;
  maxHealth: number;
  kills: number;
  deaths: number;
  pelletsCollected: number;
  scale: number;
  damageMultiplier: number;
  speedMultiplier: number;
  lastMineDeployAt: number;
  lastBoostAt: number;
  color?: string;
}

// Type definitions
interface Player {
  pos: Vec2;
  vel: Vec2;
  rotation: number;
  targetRotation: number;
  angularVelocity: number;
  health: number;
  maxHealth: number;
  kills: number;
  deaths: number;
  name: string;
  pelletsCollected: number;
  scale: number;
  damageMultiplier: number;
  speedMultiplier: number;
}

const $ = (id: string): HTMLElement | null => document.getElementById(id);

const canvas = $("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d", { alpha: false })!;

let unifiedPostProcess: UnifiedPostProcessEffects | null = null;
let gpuExplosionParticles: GPUExplosionParticles | null = null;

// Status bar element references
const lobbyInfoEl = $("lobby-info");
const lobbyIdEl = $("lobby-id");
const gameStatsEl = $("game-stats");
const playerXEl = $("player-x");
const playerYEl = $("player-y");
const fpsCounterEl = $("fps-counter");
const perfStatsEl = $("perf-stats");

const bugReportBtnEl = $("bugReportBtn") as HTMLSpanElement;

// Top bar element references
const topbarEl = $("topbar");
const topbarKillsEl = $("topbar-kills");
const topbarPelletsEl = $("topbar-pellets");
const topbarHealthEl = $("topbar-health");
const topbarMaxHealthEl = $("topbar-maxhealth");
const topbarDamageEl = $("topbar-damage");
const topbarSpeedEl = $("topbar-speed");

// Leaderboard is now handled by dedicated UI component

// Auth panel element references removed - now using event delegation and dynamic queries
// since React portal renders these elements after initial load

// Authentication state
const authState = {
  token: null as string | null,
  username: localStorage.getItem("username") || null,
  avatarUrl: "",
  isGuest: false,
  isPaid: localStorage.getItem("isPaid") === "true",
  stats: {
    kills: 0,
    deaths: 0,
    pellets: 0,
    wins: 0,
    games: 0,
    playtime: 0,
  },
};

const state = {
  playerId: null as string | null,
  lobbyId: null as string | null,
  started: false,
  connected: false,
  connectionStatus: "CONNECTING" as
    | "ONLINE"
    | "OFFLINE"
    | "CONNECTING"
    | "ERROR",
  players: new Map<string, ClientPlayer>(),
  playersTarget: new Map<string, ClientPlayer>(),
  lobbyPlayers: [] as Array<{
    id: string;
    name: string;
    color: string;
    ready: boolean;
  }>,
  bullets: [] as Bullet[],
  bulletsInterpolation: new Map<string, BulletInterpolation>(),
  mines: [] as Mine[],
  flags: [] as Array<{
    id: string;
    teamId: number;
    pos: { x: number; y: number };
    state: "home" | "carried" | "dropped";
    carrierId?: string;
    droppedAt?: number;
  }>,
  gameModeState: null as {
    mode: string;
    teamScores?: Record<number, number>;
    captureLimit?: number;
    flags?: Array<{
      id: string;
      teamId: number;
      state: "home" | "carried" | "dropped";
      carrierId?: string;
    }>;
  } | null,
  asteroids: new Map<string, Asteroid>(),
  asteroidsTarget: new Map<string, Asteroid>(),
  pellets: new Map<string, Pellet>(),
  pelletsTarget: new Map<string, Pellet>(),
  world: WORLD,
  camera: { x: 0, y: 0 },
  parallaxCamera: { pos: { x: 0, y: 0 } },
  party: null as any | null, // Party state from realtime backend
  lastStateAt: 0,
  fps: 0,
  lastFpsUpdate: 0,
  frameCount: 0,
  stars: [] as Star[],
  lastPingTime: 0,
  lastFrameTime: 0,
  performance: null as PerformanceMetrics | null,
  smoothedMetrics: {
    tps: 0,
    frameTime: 0,
    entities: {
      players: 0,
      bullets: 0,
      asteroids: 0,
      pellets: 0,
    },
    ping: 0,
    lastUpdate: 0,
  },
  lastAcknowledgedSequence: 0,
  predictedPlayer: null as Player | null,
  serverPlayer: null as Player | null,
  // Visual position for smooth rendering (separate from predicted position)
  visualPlayer: null as {
    pos: Vec2;
    vel: Vec2;
    rotation: number;
  } | null,
};

let realtimeBackend: RealtimeBackend = createStdbRealtimeBackendFromBindings(
  resolveStdbRuntimeConfig(),
);
let detachRealtimeEventBridge: (() => void) | null = null;

function handleConnectionLifecycleState(
  connectionState: "connected" | "disconnected" | "error",
): void {
  if (connectionState === "connected") {
    trackConnectionState(true);
    resetDisconnectTracking();
    gameActions.setGlobalState({
      connectionStatus: "ONLINE",
      isConnected: true,
    });
    showToast("Connected to server", "success");
    requestLobbyList();
    return;
  }

  if (connectionState === "disconnected") {
    trackConnectionState(false);
    gameActions.setGlobalState({
      connectionStatus: "OFFLINE",
      isConnected: false,
    });
    updateLobbyDisplay(null);
    updateButtons(false);
    if (gameStatsEl) gameStatsEl.style.display = "none";
    if (perfStatsEl) perfStatsEl.style.display = "none";
    showToast("Disconnected from server", "error");
    return;
  }

  trackConnectionState(false);
  gameActions.setGlobalState({
    connectionStatus: "ERROR",
    isConnected: false,
  });
  showToast("Connection error occurred", "error");
}

function bindRealtimeBackend(backend: RealtimeBackend): void {
  if (detachRealtimeEventBridge) {
    detachRealtimeEventBridge();
  }
  realtimeBackend = backend;
  setActiveRealtimeBackend(backend);
  detachRealtimeEventBridge = backend.onEvent((event) => {
    if (event.type === "connection_state") {
      handleConnectionLifecycleState(event.state);
      return;
    }

    const mappedMessages = mapRealtimeEventToLegacyMessages(event, {
      currentLobbyId: state.lobbyId,
      currentPlayerId: state.playerId,
    });
    for (const message of mappedMessages) {
      dispatchLocalMessage(message);
    }
  });
}

// Route realtime commands through the migration seam.
bindRealtimeBackend(realtimeBackend);

// NEW: Strafe mode state and thrust trails
// const strafeState: StrafeState = createStrafeState(); // Unused currently
const thrustTrails = new Map<string, ThrustTrail>();

// Set up state for extracted interpolation modules
setBulletInterpolationState(state);
setPlayerInterpolationState(state);
setPredictOwnPlayerFunction(predictOwnPlayer);

// Initialize font CSS variables from centralized config
initializeFontCSS();

// Initialize global overlay (scanlines, grain, vignette, screendoor) on top of everything
const globalOverlay = new GlobalOverlay();
globalOverlay.mount(document.body);

// Audio functions have been extracted to src/audio/audio.client.ts

// Track previous pellet count to detect collection
const previousPelletsCollected = 0;
const previousHealth = 0;
const previousBoostAt = 0;

// Screen effects state
const screenShake = { offsetX: 0, offsetY: 0, intensity: 0 };
const screenFlash = { intensity: 0, r: 1, g: 1, b: 1 };

// Trigger screen shake
export function triggerScreenShake(intensity: number = 10): void {
  screenShake.intensity = Math.max(screenShake.intensity, intensity);
  screenShake.offsetX = (Math.random() - 0.5) * intensity;
  screenShake.offsetY = (Math.random() - 0.5) * intensity;
}

// Trigger screen flash
export function triggerScreenFlash(
  intensity: number = 0.5,
  r: number = 1,
  g: number = 1,
  b: number = 1,
): void {
  screenFlash.intensity = Math.max(screenFlash.intensity, intensity);
  screenFlash.r = r;
  screenFlash.g = g;
  screenFlash.b = b;
}

// Initialize metrics UI
initMetricsUI();

// Initialize lobby browser UI with dependency injection
setBrowserDependencies({
  sendMessage: dispatchNetworkMessage,
  state,
  getPlayerNameInput: () => authState.username || "",
});
initLobbyBrowserUI();

// Wire input transport with current lobby identity for STDB path.
setRealtimeInputDependencies({
  getLobbyId: () => state.lobbyId,
});

// Resize canvas to window size (accounting for status bar and top bar)
function resizeCanvas() {
  canvas.width = window.innerWidth;
  const topBarHeight = state.started ? 48 : 0;
  canvas.height = window.innerHeight - 28 - topBarHeight;
  canvas.style.top = `${topBarHeight}px`;

  if (!unifiedPostProcess) {
    unifiedPostProcess = new UnifiedPostProcessEffects(
      canvas.width,
      canvas.height,
    );
  } else {
    unifiedPostProcess.resize(canvas.width, canvas.height);
  }

  if (!gpuExplosionParticles) {
    gpuExplosionParticles = new GPUExplosionParticles(
      canvas.width,
      canvas.height,
    );
  } else {
    gpuExplosionParticles.resize(canvas.width, canvas.height);
  }

  // Regenerate stars when canvas size changes
  if (state.stars.length > 0) {
    state.stars = generateStars({ width: canvas.width, height: canvas.height });
  }
}
resizeCanvas();
window.addEventListener("resize", resizeCanvas);

// Update connection status in status bar

// Update lobby display
function updateLobbyDisplay(lobbyId: string | null) {
  if (lobbyId && lobbyInfoEl && lobbyIdEl) {
    lobbyInfoEl.style.display = "flex";
    lobbyIdEl.textContent = lobbyId;
  } else if (lobbyInfoEl) {
    lobbyInfoEl.style.display = "none";
  }
}

// Update button visibility
function updateButtons(_inLobby: boolean) {
  // No buttons to update currently
}

// Smooth metric updates with exponential moving average
function updateSmoothedMetrics(
  newMetrics: Partial<PerformanceMetrics & { ping: number }>,
) {
  const now = performance.now();
  const timeSinceLastUpdate = now - state.smoothedMetrics.lastUpdate;

  // Only update every 250ms to reduce jitter
  if (timeSinceLastUpdate < 250) {
    return;
  }

  const smoothingFactor = 0.3; // Lower = smoother (0-1)

  if (state.smoothedMetrics.tps === 0) {
    // Initialize on first update
    state.smoothedMetrics.tps = newMetrics.tps || 0;
    state.smoothedMetrics.frameTime = newMetrics.frameTime || 0;
    if (newMetrics.entities) {
      state.smoothedMetrics.entities = { ...newMetrics.entities };
    }
    state.smoothedMetrics.ping = newMetrics.ping || 0;
  } else {
    // Apply exponential moving average
    state.smoothedMetrics.tps = Math.round(
      state.smoothedMetrics.tps * (1 - smoothingFactor) +
        (newMetrics.tps || state.smoothedMetrics.tps) * smoothingFactor,
    );
    state.smoothedMetrics.frameTime =
      state.smoothedMetrics.frameTime * (1 - smoothingFactor) +
      (newMetrics.frameTime || state.smoothedMetrics.frameTime) *
        smoothingFactor;

    // Smooth each entity count separately
    if (newMetrics.entities) {
      state.smoothedMetrics.entities.players = Math.round(
        state.smoothedMetrics.entities.players * (1 - smoothingFactor) +
          newMetrics.entities.players * smoothingFactor,
      );
      state.smoothedMetrics.entities.bullets = Math.round(
        state.smoothedMetrics.entities.bullets * (1 - smoothingFactor) +
          newMetrics.entities.bullets * smoothingFactor,
      );
      state.smoothedMetrics.entities.asteroids = Math.round(
        state.smoothedMetrics.entities.asteroids * (1 - smoothingFactor) +
          newMetrics.entities.asteroids * smoothingFactor,
      );
      state.smoothedMetrics.entities.pellets = Math.round(
        state.smoothedMetrics.entities.pellets * (1 - smoothingFactor) +
          newMetrics.entities.pellets * smoothingFactor,
      );
    }

    state.smoothedMetrics.ping = Math.round(
      state.smoothedMetrics.ping * (1 - smoothingFactor) +
        (newMetrics.ping || state.smoothedMetrics.ping) * smoothingFactor,
    );
  }

  state.smoothedMetrics.lastUpdate = now;

  // Update performance stats display
  updatePerformanceStatsUI(state.smoothedMetrics);
}

// Expose state globally for UI bridge access
(window as any).state = state;

/**
 * Setup party state after realtime backend is established.
 * Handles party join/create based on URL params or saved state.
 */
function setupPartyOnConnection() {
  // Check for ?party=CODE URL parameter for one-click join
  const urlParams = new URLSearchParams(window.location.search);
  const partyCodeFromUrl = urlParams.get("party");

  const savedName = authState.username || "";

  if (partyCodeFromUrl) {
    console.log(`Joining party from URL: ${partyCodeFromUrl}`);
    dispatchNetworkMessage({
      t: "party_join_code",
      joinCode: partyCodeFromUrl,
      playerName: savedName,
    });

    // Clean URL (remove ?party=CODE parameter)
    window.history.replaceState({}, document.title, window.location.pathname);
  } else {
    const savedPartyCode = localStorage.getItem("partyJoinCode");

    if (savedPartyCode) {
      // 2. Rejoin saved party after refresh (medium priority)
      console.log(`Restoring party from refresh: ${savedPartyCode}`);
      dispatchNetworkMessage({
        t: "party_join_code",
        joinCode: savedPartyCode,
        playerName: savedName,
      });
    } else {
      // 3. Create new solo party (lowest priority)
      console.log(`Creating solo party for: ${savedName || "Player"}`);
      dispatchNetworkMessage({
        t: "party_create",
        playerName: savedName,
      });
    }
  }
}

const AUTH_BOOTSTRAP_BUDGET_MS = 2000;

function ensureFallbackCallsign(): void {
  if (authState.username && authState.username.trim().length > 0) {
    return;
  }

  const persistedName = localStorage.getItem("username")?.trim() ?? "";
  const generatedName =
    persistedName.length > 0
      ? persistedName
      : `Pilot-${Math.floor(1000 + Math.random() * 9000)}`;
  authState.username = generatedName;
  localStorage.setItem("username", generatedName);
}

/**
 * Initialize authentication before connecting realtime backend so party setup
 * uses the current identity.
 */
async function initializeApp() {
  console.log("Initializing authentication...");

  // Keep startup responsive even if auth endpoints are slow/unavailable.
  const authBootstrapOutcome = await runAuthBootstrap(
    initializeAuth,
    AUTH_BOOTSTRAP_BUDGET_MS,
  );
  if (authBootstrapOutcome === "timed_out") {
    console.warn(
      `Auth bootstrap exceeded ${AUTH_BOOTSTRAP_BUDGET_MS}ms, continuing startup.`,
    );
  } else if (authBootstrapOutcome === "failed") {
    console.warn("Auth bootstrap failed, continuing startup.");
  }
  ensureFallbackCallsign();

  try {
    const persistedStdbToken =
      localStorage.getItem("stdb_auth_token") ?? undefined;
    await realtimeBackend.connect({
      token: persistedStdbToken ?? authState.token ?? undefined,
    });
    console.log("SpaceTimeDB realtime backend connected");

    // Realtime transport is authoritative for lobby/party bootstrap.
    setupPartyOnConnection();
    requestLobbyList();
    gameActions.setGlobalState({
      connectionStatus: "ONLINE",
      isConnected: true,
    });
    showToast("Connected to SpaceTimeDB", "success");
  } catch (error) {
    console.error("Failed to connect SpaceTimeDB backend:", error);
    gameActions.setGlobalState({
      connectionStatus: "ERROR",
      isConnected: false,
    });
    showToast("Failed to connect to SpaceTimeDB", "error");
  }

  // Show join modal after initialization completes.
  showJoinModal();
}

// Start app initialization
initializeApp();

// Setup console log redirection to server for debugging
const originalConsoleLog = console.log;
setupConsoleRedirection(originalConsoleLog);

// Handle "Join New Game" button in death modal
const joinNewGameBtn = $("joinNewGameBtn") as HTMLButtonElement;
if (joinNewGameBtn) {
  joinNewGameBtn.onclick = handleJoinNewGame;
}

// Retry playing theme music on user interaction if it's not playing
document.addEventListener(
  "click",
  () => {
    const themeMusic = getThemeMusic();
    if (themeMusic?.paused && state.connected) {
      console.log("Retrying theme music after user interaction...");
      themeMusic
        .play()
        .then(() => {
          console.log("Theme music started after user interaction");
        })
        .catch(() => {
          // Still failed, don't log to avoid spam
        });
    }
  },
  { once: false },
);

// Predictive interpolation for asteroid positions using velocity data
function interpolateAsteroids() {
  const now = performance.now();
  const timeSinceUpdate = now - state.lastStateAt;
  const predictTime = timeSinceUpdate / 1000; // Convert to seconds for velocity calc
  const { w, h } = state.world;

  for (const [id, target] of state.asteroidsTarget) {
    const current = state.asteroids.get(id);
    if (current && target) {
      // Skip interpolation for off-screen asteroids (performance optimization)
      if (!isVisible(current.pos.x, current.pos.y, 300)) {
        current.pos.x = target.pos.x;
        current.pos.y = target.pos.y;
        current.rotation = target.rotation;
        current.health = target.health;
        current.maxHealth = target.maxHealth;
        continue;
      }

      // Predict where asteroid should be based on velocity
      let predictedX = target.pos.x + target.vel.x * predictTime;
      let predictedY = target.pos.y + target.vel.y * predictTime;

      // Handle world wrapping for predicted position
      predictedX = wrapCoordinate(predictedX, w);
      predictedY = wrapCoordinate(predictedY, h);

      // Predict and normalize rotation based on rotationSpeed
      // Server rotation is unbounded, normalize before smoothing
      const predictedRotation = normalizeRotation(
        target.rotation + target.rotationSpeed * predictTime,
      );

      // Use common smoothing function
      smoothPosition({
        current: current.pos,
        target: { x: predictedX, y: predictedY },
        world: state.world,
      });

      // Smooth rotation with shared utility
      current.rotation = smoothRotation(
        current.rotation,
        predictedRotation,
        0.225,
      );

      // Update non-positional data directly
      current.health = target.health;
      current.maxHealth = target.maxHealth;
    }
  }
}

// Interpolate pellet positions with wrapping
function interpolatePellets() {
  // Update each pellet position smoothly
  for (const [id, target] of state.pelletsTarget) {
    const current = state.pellets.get(id);
    if (current && target) {
      // Skip interpolation for off-screen pellets (with margin for smooth entry)
      if (!isVisible(current.pos.x, current.pos.y, 200)) {
        // Just update position directly from server for off-screen pellets
        current.pos.x = target.pos.x;
        current.pos.y = target.pos.y;
        current.age = target.age;
        continue;
      }

      // Use common smoothing function (pellets don't have rotation)
      smoothPosition({
        current: current.pos,
        target: target.pos,
        world: state.world,
      });

      // Update age directly from target
      current.age = target.age;
    }
  }
}

// Calculate FPS
function updateFPS() {
  const now = performance.now();
  state.frameCount++;
  if (now - state.lastFpsUpdate >= 500) {
    // Update every 500ms for smoother display
    state.fps = Math.round(state.frameCount * 2); // Multiply by 2 since we're measuring over 500ms
    state.frameCount = 0;
    state.lastFpsUpdate = now;
    // Update FPS counter in status bar
    if (fpsCounterEl) {
      fpsCounterEl.textContent = state.fps.toString();
      fpsCounterEl.className =
        state.fps >= 55
          ? "value good"
          : state.fps >= 45
            ? "value warning"
            : "value bad";
    }
  }
}

// Update top bar stats
function updateTopBarStats(player: Player) {
  if (player) {
    if (topbarKillsEl) {
      topbarKillsEl.textContent = (player.kills || 0).toString();
    }
    if (topbarPelletsEl) {
      topbarPelletsEl.textContent = (player.pelletsCollected || 0).toString();
    }
    if (topbarHealthEl) {
      topbarHealthEl.textContent = Math.ceil(player.health || 0).toString();
    }
    if (topbarMaxHealthEl) {
      topbarMaxHealthEl.textContent = (player.maxHealth || 100).toString();
    }
    // Show actual damage: base damage (1) × multiplier (using Math.ceil like server)
    const actualDamage = Math.ceil(1 * (player.damageMultiplier || 1));
    if (topbarDamageEl) topbarDamageEl.textContent = actualDamage.toString();
    if (topbarSpeedEl) {
      topbarSpeedEl.textContent = (player.speedMultiplier || 1).toFixed(1);
    }
  }
}

// Update camera to follow player with smooth wrapping
function updateCamera() {
  const myPlayer = state.playerId ? state.players.get(state.playerId) : null;
  if (myPlayer) {
    const { w, h } = state.world;

    // Update top bar stats
    updateTopBarStats(myPlayer);

    // Calculate wrapped distance from camera to player
    let dx = myPlayer.pos.x - state.camera.x;
    let dy = myPlayer.pos.y - state.camera.y;

    // Handle wrapping - take shortest path
    if (dx > w / 2) dx -= w;
    if (dx < -w / 2) dx += w;
    if (dy > h / 2) dy -= h;
    if (dy < -h / 2) dy += h;

    // Update parallax camera position (unwrapped, continuous)
    state.parallaxCamera.pos.x += dx;
    state.parallaxCamera.pos.y += dy;

    // Update camera position with wrapped distance
    state.camera.x += dx;
    state.camera.y += dy;

    // Wrap camera position within world bounds
    if (state.camera.x < 0) state.camera.x += w;
    if (state.camera.x >= w) state.camera.x -= w;
    if (state.camera.y < 0) state.camera.y += h;
    if (state.camera.y >= h) state.camera.y -= h;

    // Update player coordinates in status bar (only when significantly changed)
    if (playerXEl) {
      const newX = Math.floor(myPlayer.pos.x).toString();
      if (playerXEl.textContent !== newX) {
        playerXEl.textContent = newX;
      }
    }
    if (playerYEl) {
      const newY = Math.floor(myPlayer.pos.y).toString();
      if (playerYEl.textContent !== newY) {
        playerYEl.textContent = newY;
      }
    }
  }
}

// Calculate wrapped position relative to camera
function getWrappedPosition(worldX: number, worldY: number) {
  const { w, h } = state.world;
  let dx = worldX - state.camera.x;
  let dy = worldY - state.camera.y;

  // Wrap distances to find shortest path
  if (dx > w / 2) dx -= w;
  if (dx < -w / 2) dx += w;
  if (dy > h / 2) dy -= h;
  if (dy < -h / 2) dy += h;

  return {
    x: state.camera.x + dx,
    y: state.camera.y + dy,
  };
}

// Convert world coordinates to screen coordinates with wrapping
function worldToScreen(worldX: number, worldY: number) {
  const wrapped = getWrappedPosition(worldX, worldY);
  return {
    x: wrapped.x - state.camera.x + canvas.width / 2,
    y: wrapped.y - state.camera.y + canvas.height / 2,
  };
}

// Check if object is visible on screen
function isVisible(worldX: number, worldY: number, margin = 100) {
  const screen = worldToScreen(worldX, worldY);
  return (
    screen.x >= -margin &&
    screen.x <= canvas.width + margin &&
    screen.y >= -margin &&
    screen.y <= canvas.height + margin
  );
}

// Client-side prediction and reconciliation functions
function predictOwnPlayer() {
  if (!state.playerId || !state.serverPlayer) return;

  // Start from the last authoritative server position
  const predicted = { ...state.serverPlayer };

  // Find inputs after the last acknowledged sequence
  const unacknowledgedInputs = getInputBuffer().filter(
    (frame) => frame.sequence > state.lastAcknowledgedSequence,
  );

  // Apply each unacknowledged input using per-tick physics (matches server exactly)
  for (const frame of unacknowledgedInputs) {
    const rotResult = computeTickRotation({
      currentRotation: predicted.rotation,
      mouseScreenX: frame.input.mouseScreenX,
      mouseScreenY: frame.input.mouseScreenY,
      canvasWidth: frame.input.canvasWidth,
      canvasHeight: frame.input.canvasHeight,
      maxRotationPerTick: MOUSE_MAX_ROTATION_PER_TICK,
      snapThreshold: ROTATION_SNAP_THRESHOLD_TICKS,
    });
    predicted.rotation = rotResult.rotation;
    predicted.targetRotation = rotResult.targetRotation;

    const boostActive = frame.input.keyBoost;
    const speedMult = predicted.speedMultiplier || 1;
    const boostMult = boostActive ? PLAYER_BOOST_MULTIPLIER : 1;

    const moveResult = computeTickMovement({
      rotation: predicted.rotation,
      currentVelX: predicted.vel.x,
      currentVelY: predicted.vel.y,
      posX: predicted.pos.x,
      posY: predicted.pos.y,
      keyForward: frame.input.keyForward,
      keyBack: frame.input.keyBack,
      keyLeft: frame.input.keyLeft,
      keyRight: frame.input.keyRight,
      thrustPerTick: PLAYER_THRUST_PER_TICK * boostMult * speedMult,
      maxSpeed: PLAYER_BASE_SPEED_PER_TICK * boostMult * speedMult,
      strafeMultiplier: STRAFE_THRUST_MULTIPLIER,
      friction: FRICTION,
      worldWidth: WORLD.w,
      worldHeight: WORLD.h,
    });
    predicted.pos = { x: moveResult.posX, y: moveResult.posY };
    predicted.vel = { x: moveResult.velX, y: moveResult.velY };
  }

  // Store predicted state
  state.predictedPlayer = predicted;
}

function reconcileWithServer(
  serverPlayer: Player,
  acknowledgedSequence: number,
) {
  // Store server state
  state.serverPlayer = { ...serverPlayer };
  state.lastAcknowledgedSequence = acknowledgedSequence;

  // Remove acknowledged inputs from buffer
  removeAcknowledgedInputs(acknowledgedSequence);

  // Re-predict from new server state
  predictOwnPlayer();
}

function renderPelletsWithFade(renderCtx: CanvasRenderingContext2D) {
  const time = Date.now() * 0.001; // Time for twinkle animation

  for (const pellet of state.pellets.values()) {
    if (isVisible(pellet.pos.x, pellet.pos.y)) {
      const fadeStartPercent = 0.75;
      const agePercent = pellet.age / pellet.lifetime;
      let alpha = 1.0;
      if (agePercent > fadeStartPercent) {
        const fadeProgress =
          (agePercent - fadeStartPercent) / (1.0 - fadeStartPercent);
        alpha = Math.max(0, 1 - fadeProgress);
      }

      // Twinkle effect: pulsing size and brightness
      const twinkleSpeed = 3.0 + (pellet.value % 10) * 0.1; // Vary speed per pellet
      const twinkleFactor =
        0.5 + 0.5 * Math.sin(time * twinkleSpeed + pellet.pos.x + pellet.pos.y);
      const sizePulse = 1.0 + twinkleFactor * 0.3; // 30% size variation
      const brightnessPulse = 0.7 + twinkleFactor * 0.3; // Brightness variation

      const baseRadius = Math.min(2 + Math.log10(pellet.value) * 2, 8);
      const outerRadius = baseRadius * sizePulse;
      const glowRadius = outerRadius * 3; // Large glow radius

      // Much brighter colors with higher saturation
      let red: number, green: number, blue: number;
      if (pellet.value >= 1000) {
        // Gold - ultra bright
        red = 255;
        green = 255;
        blue = 100;
      } else if (pellet.value >= 100) {
        // Purple - much brighter
        red = 220;
        green = 120;
        blue = 255;
      } else if (pellet.value >= 10) {
        // Blue - much brighter
        red = 120;
        green = 200;
        blue = 255;
      } else {
        // Green - much brighter
        red = 100;
        green = 255;
        blue = 150;
      }

      const wrapped = getWrappedPosition(pellet.pos.x, pellet.pos.y);

      renderCtx.save();
      renderCtx.globalAlpha = alpha * brightnessPulse;

      // Outer glow (large, soft)
      const gradient = renderCtx.createRadialGradient(
        wrapped.x,
        wrapped.y,
        0,
        wrapped.x,
        wrapped.y,
        glowRadius,
      );
      gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, 0.8)`);
      gradient.addColorStop(0.3, `rgba(${red}, ${green}, ${blue}, 0.4)`);
      gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0)`);

      renderCtx.fillStyle = gradient;
      renderCtx.beginPath();
      renderCtx.arc(wrapped.x, wrapped.y, glowRadius, 0, Math.PI * 2);
      renderCtx.fill();

      // Strong shadow blur for extra glow
      renderCtx.shadowBlur = 20;
      renderCtx.shadowColor = `rgb(${red}, ${green}, ${blue})`;

      // Core pellet (bright and solid)
      renderCtx.fillStyle = `rgb(${Math.min(255, red * 1.2)}, ${Math.min(255, green * 1.2)}, ${Math.min(255, blue * 1.2)})`;
      renderCtx.beginPath();
      renderCtx.arc(wrapped.x, wrapped.y, outerRadius, 0, Math.PI * 2);
      renderCtx.fill();

      // Bright center highlight
      renderCtx.shadowBlur = 0;
      renderCtx.fillStyle = `rgba(255, 255, 255, ${0.8 * brightnessPulse})`;
      renderCtx.beginPath();
      renderCtx.arc(wrapped.x, wrapped.y, outerRadius * 0.4, 0, Math.PI * 2);
      renderCtx.fill();

      renderCtx.restore();
    }
  }
}

function renderAsteroidsWithHealthBars(renderCtx: CanvasRenderingContext2D) {
  renderCtx.lineWidth = 1;
  for (const [, a] of state.asteroids) {
    if (isVisible(a.pos.x, a.pos.y)) {
      const wrapped = getWrappedPosition(a.pos.x, a.pos.y);
      const radius = ASTEROID_SIZES[a.sizeLevel] || 20;
      const healthRatio = a.health / a.maxHealth;
      const hash = a.id.split("").reduce((a: number, b: string) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);

      renderCtx.strokeStyle = "#ffffff";
      renderCtx.save();
      renderCtx.translate(wrapped.x, wrapped.y);
      renderCtx.rotate(a.rotation);
      renderCtx.lineWidth = 2;
      renderCtx.beginPath();
      const sides = 8;
      for (let i = 0; i < sides; i++) {
        const angle = (i / sides) * Math.PI * 2;
        const variation =
          0.7 + ((Math.abs(hash + i * 123) % 1000) / 1000) * 0.6;
        const r = radius * variation;
        const x = Math.cos(angle) * r;
        const y = Math.sin(angle) * r;
        if (i === 0) {
          renderCtx.moveTo(x, y);
        } else {
          renderCtx.lineTo(x, y);
        }
      }
      renderCtx.closePath();
      renderCtx.stroke();
      renderCtx.restore();

      if (healthRatio < 1) {
        const barWidth = radius * 1.5;
        const barHeight = 4;
        const barY = wrapped.y - radius - 10;
        renderCtx.fillStyle = "#333";
        renderCtx.fillRect(wrapped.x - barWidth / 2, barY, barWidth, barHeight);
        renderCtx.fillStyle =
          healthRatio > 0.5
            ? "#4ade80"
            : healthRatio > 0.25
              ? "#fbbf24"
              : "#ef4444";
        renderCtx.fillRect(
          wrapped.x - barWidth / 2,
          barY,
          barWidth * healthRatio,
          barHeight,
        );
      }
    }
  }
}

function renderPlayersWithNameplates(renderCtx: CanvasRenderingContext2D) {
  for (const [id, p] of state.players) {
    if (!isVisible(p.pos.x, p.pos.y)) continue;
    const wrapped = getWrappedPosition(p.pos.x, p.pos.y);
    const isMe = id === state.playerId;
    const isDead = p.health <= 0;

    if (!isDead) {
      const baseRadius = isMe ? 10 : 8;
      const scaledRadius = baseRadius * (p.scale || 1.0);

      // Check if this player is carrying a flag
      const carriedFlag = state.flags.find(
        (flag) => flag.state === "carried" && flag.carrierId === id,
      );

      renderCtx.save();

      // Draw flag carrier indicator BEFORE the player (so it's behind)
      if (carriedFlag) {
        const flagColor = carriedFlag.teamId === 0 ? COLORS.BLUE : COLORS.RED;
        const pulsePhase = (Date.now() % 1500) / 1500;
        const pulseRadius = 40 + 8 * Math.sin(pulsePhase * Math.PI * 2);
        const pulseAlpha = 0.4 + 0.3 * Math.sin(pulsePhase * Math.PI * 2);

        // Outer pulsing circle
        renderCtx.strokeStyle = flagColor;
        renderCtx.lineWidth = 3;
        renderCtx.globalAlpha = pulseAlpha;
        renderCtx.beginPath();
        renderCtx.arc(wrapped.x, wrapped.y, pulseRadius, 0, Math.PI * 2);
        renderCtx.stroke();

        // Inner solid circle
        renderCtx.lineWidth = 2;
        renderCtx.globalAlpha = 0.5;
        renderCtx.beginPath();
        renderCtx.arc(wrapped.x, wrapped.y, 30, 0, Math.PI * 2);
        renderCtx.stroke();

        renderCtx.globalAlpha = 1.0;

        // Draw mini flag icon above player
        const flagX = wrapped.x + scaledRadius * 1.5;
        const flagY = wrapped.y - scaledRadius * 2;

        // Flag pole
        renderCtx.strokeStyle = "#DDDDDD";
        renderCtx.lineWidth = 2;
        renderCtx.beginPath();
        renderCtx.moveTo(flagX, flagY);
        renderCtx.lineTo(flagX, flagY - 20);
        renderCtx.stroke();

        // Flag fabric
        renderCtx.fillStyle = flagColor;
        renderCtx.strokeStyle = flagColor;
        renderCtx.lineWidth = 1;
        renderCtx.beginPath();
        renderCtx.moveTo(flagX, flagY - 20);
        renderCtx.lineTo(flagX + 15, flagY - 13);
        renderCtx.lineTo(flagX, flagY - 6);
        renderCtx.closePath();
        renderCtx.fill();
        renderCtx.stroke();

        // Flag border
        renderCtx.strokeStyle = "#FFFFFF";
        renderCtx.lineWidth = 0.5;
        renderCtx.stroke();
      }

      // Draw player ship
      renderCtx.translate(wrapped.x, wrapped.y);
      renderCtx.rotate(p.rotation || 0);
      const height = scaledRadius * 1.2;
      const width = scaledRadius * 1.5;
      renderCtx.beginPath();
      renderCtx.moveTo(width * 0.6, 0);
      renderCtx.lineTo(-width * 0.4, -height * 0.5);
      renderCtx.lineTo(-width * 0.4, height * 0.5);
      renderCtx.closePath();
      renderCtx.strokeStyle = p.color || (isMe ? COLORS.PLAYER : COLORS.ENEMY);
      renderCtx.lineWidth = 2;
      renderCtx.stroke();
      renderCtx.restore();

      renderCtx.font = `bold 10px ${FONT_FAMILY_CANVAS}`;
      renderCtx.fillStyle = isMe ? "#4ade80" : "#e8e8e8";
      renderCtx.textAlign = "center";
      renderCtx.textBaseline = "bottom";
      const insigniaY = wrapped.y - scaledRadius * 1.5 - 30;
      drawRankInsignia(
        renderCtx,
        wrapped.x,
        insigniaY,
        p.pelletsCollected || 0,
        isMe,
      );
      renderCtx.fillStyle = p.color || (isMe ? "#4ade80" : "#e8e8e8");
      const nameY = wrapped.y - scaledRadius * 1.5 - 8;
      renderCtx.fillText(p.name || "Player", wrapped.x, nameY);

      const barWidth = 40;
      const barHeight = 4;
      const barY = wrapped.y - scaledRadius * 1.5 - 2;
      renderCtx.fillStyle = "#374151";
      renderCtx.fillRect(wrapped.x - barWidth / 2, barY, barWidth, barHeight);
      const healthPct = p.health / p.maxHealth;
      renderCtx.fillStyle =
        healthPct > 0.5 ? "#4ade80" : healthPct > 0.25 ? "#fbbf24" : "#ef4444";
      renderCtx.fillRect(
        wrapped.x - barWidth / 2,
        barY,
        barWidth * healthPct,
        barHeight,
      );
      renderCtx.textAlign = "left";
    } else {
      renderCtx.strokeStyle = "#6b7280";
      renderCtx.lineWidth = 2;
      renderCtx.beginPath();
      renderCtx.moveTo(wrapped.x - 5, wrapped.y - 5);
      renderCtx.lineTo(wrapped.x + 5, wrapped.y + 5);
      renderCtx.moveTo(wrapped.x + 5, wrapped.y - 5);
      renderCtx.lineTo(wrapped.x - 5, wrapped.y + 5);
      renderCtx.stroke();
    }
  }
}

/**
 * Renders CTF flags with visual indicators for different states
 */
function renderFlags(renderCtx: CanvasRenderingContext2D) {
  for (const flag of state.flags) {
    // Skip carried flags (they're rendered with the carrier)
    if (flag.state === "carried") continue;

    // Skip if not visible on screen
    if (!isVisible(flag.pos.x, flag.pos.y)) continue;

    // Get wrapped position for toroidal world
    const wrapped = getWrappedPosition(flag.pos.x, flag.pos.y);

    const flagColor = flag.teamId === 0 ? COLORS.BLUE : COLORS.RED;

    renderCtx.save();

    // Draw glow/shadow for better visibility
    renderCtx.shadowColor = flagColor;
    renderCtx.shadowBlur = 20;

    // Draw flag pole (thicker and more visible)
    renderCtx.strokeStyle = "#DDDDDD";
    renderCtx.lineWidth = 4;
    renderCtx.beginPath();
    renderCtx.moveTo(wrapped.x, wrapped.y);
    renderCtx.lineTo(wrapped.x, wrapped.y - 50);
    renderCtx.stroke();

    // Draw flag fabric with team color (larger and more prominent)
    renderCtx.fillStyle = flagColor;
    renderCtx.strokeStyle = flagColor;
    renderCtx.lineWidth = 2;
    renderCtx.beginPath();
    renderCtx.moveTo(wrapped.x, wrapped.y - 50);
    renderCtx.lineTo(wrapped.x + 40, wrapped.y - 37);
    renderCtx.lineTo(wrapped.x, wrapped.y - 24);
    renderCtx.closePath();
    renderCtx.fill();
    renderCtx.stroke();

    // Add a bright border to the flag for visibility
    renderCtx.strokeStyle = "#FFFFFF";
    renderCtx.lineWidth = 1;
    renderCtx.stroke();

    // Clear shadow for state indicators
    renderCtx.shadowBlur = 0;

    // Add pulsing glow for dropped flags
    if (flag.state === "dropped") {
      const pulsePhase = (Date.now() % 1000) / 1000;
      const pulseAlpha = 0.3 + 0.4 * Math.sin(pulsePhase * Math.PI * 2);

      renderCtx.strokeStyle = "#FFFF00";
      renderCtx.lineWidth = 3;
      renderCtx.globalAlpha = pulseAlpha;
      renderCtx.beginPath();
      renderCtx.arc(wrapped.x, wrapped.y - 30, 35, 0, Math.PI * 2);
      renderCtx.stroke();
      renderCtx.globalAlpha = 1.0;

      // Draw "DROPPED" text
      renderCtx.fillStyle = "#FFFF00";
      renderCtx.font = "bold 12px 'Space Mono', monospace";
      renderCtx.textAlign = "center";
      renderCtx.fillText("DROPPED", wrapped.x, wrapped.y + 20);
    }

    renderCtx.restore();
  }
}

function renderCTFBases(renderCtx: CanvasRenderingContext2D) {
  // Only render bases if in CTF mode
  if (!state.gameModeState || state.gameModeState.mode !== "ctf") return;

  const CAPTURE_RADIUS = 50; // Smaller capture zone
  const SPAWN_RADIUS = 500; // Larger spawn zone (10x)
  const basePositions = [
    { x: 0, y: 0, teamId: 0 }, // Team 0 (Blue)
    { x: state.world.w * 0.5, y: state.world.h * 0.5, teamId: 1 }, // Team 1 (Red)
  ];

  for (const base of basePositions) {
    // Skip if not visible on screen
    if (!isVisible(base.x, base.y, SPAWN_RADIUS * 1.2)) continue;

    const wrapped = getWrappedPosition(base.x, base.y);
    const baseColor = base.teamId === 0 ? COLORS.BLUE : COLORS.RED;

    renderCtx.save();

    // Draw outer spawn zone ring (large, subtle)
    renderCtx.strokeStyle = baseColor;
    renderCtx.lineWidth = 2;
    renderCtx.globalAlpha = 0.2;
    renderCtx.beginPath();
    renderCtx.arc(wrapped.x, wrapped.y, SPAWN_RADIUS, 0, Math.PI * 2);
    renderCtx.stroke();

    // Draw inner capture zone ring (small, bright)
    renderCtx.strokeStyle = baseColor;
    renderCtx.lineWidth = 3;
    renderCtx.globalAlpha = 0.5;
    renderCtx.beginPath();
    renderCtx.arc(wrapped.x, wrapped.y, CAPTURE_RADIUS, 0, Math.PI * 2);
    renderCtx.stroke();

    // Draw pulsing inner circle (within capture zone)
    const pulsePhase = (Date.now() % 2000) / 2000; // 2 second pulse cycle
    const pulseRadius =
      CAPTURE_RADIUS * 0.7 * (0.9 + 0.1 * Math.sin(pulsePhase * Math.PI * 2));
    renderCtx.strokeStyle = baseColor;
    renderCtx.lineWidth = 1;
    renderCtx.globalAlpha = 0.4 + 0.2 * Math.sin(pulsePhase * Math.PI * 2);
    renderCtx.beginPath();
    renderCtx.arc(wrapped.x, wrapped.y, pulseRadius, 0, Math.PI * 2);
    renderCtx.stroke();

    // Draw center marker
    renderCtx.fillStyle = baseColor;
    renderCtx.globalAlpha = 0.5;
    renderCtx.beginPath();
    renderCtx.arc(wrapped.x, wrapped.y, 5, 0, Math.PI * 2);
    renderCtx.fill();

    // Draw "BASE" label
    renderCtx.globalAlpha = 0.6;
    renderCtx.fillStyle = "#FFFFFF";
    renderCtx.font = "bold 12px 'Space Mono', monospace";
    renderCtx.textAlign = "center";
    renderCtx.fillText(
      `TEAM ${base.teamId} BASE`,
      wrapped.x,
      wrapped.y - CAPTURE_RADIUS - 10,
    );

    renderCtx.restore();
  }
}

function render() {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const renderTarget = ctx;

  renderStars(
    state.stars,
    state.parallaxCamera,
    { width: canvas.width, height: canvas.height },
    renderTarget,
  );

  renderTarget.save();
  renderTarget.translate(
    canvas.width / 2 - state.camera.x + screenShake.offsetX,
    canvas.height / 2 - state.camera.y + screenShake.offsetY,
  );

  renderBulletsWithTrails(renderTarget, state, isVisible, getWrappedPosition);
  renderMines(renderTarget, state.mines, state.players, {
    isVisible,
    getWrappedPosition,
    playerId: state.playerId || undefined,
  });
  renderPelletsWithFade(renderTarget);
  renderAsteroidsWithHealthBars(renderTarget);
  renderPlayersWithNameplates(renderTarget);
  renderFlags(renderTarget);
  renderCTFBases(renderTarget);

  // Render thrust trails
  thrustTrails.forEach((trail) => {
    const player = state.players.get(trail.playerId);
    const playerScale = player?.scale || 1.0;
    renderThrustTrail(
      renderTarget,
      trail,
      isVisible,
      getWrappedPosition,
      playerScale,
    );
  });

  renderTarget.restore();

  damageNumberManager.renderAll(renderTarget);

  // Render GPU explosion particles (before post-processing so they get bloomed)
  if (gpuExplosionParticles) {
    gpuExplosionParticles.render(ctx, state.camera.x, state.camera.y);
  }

  // Apply unified post-processing: bloom + all effects
  if (unifiedPostProcess) {
    unifiedPostProcess.applyEffects(canvas, ctx);
  }

  // Apply screen flash overlay
  if (screenFlash.intensity > 0) {
    ctx.save();
    ctx.fillStyle = `rgba(${Math.floor(screenFlash.r * 255)}, ${Math.floor(screenFlash.g * 255)}, ${Math.floor(screenFlash.b * 255)}, ${screenFlash.intensity})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Decay flash
    screenFlash.intensity = Math.max(0, screenFlash.intensity - 0.05);
  }

  // Decay screen shake
  if (screenShake.intensity > 0) {
    screenShake.intensity *= 0.85;
    screenShake.offsetX *= 0.85;
    screenShake.offsetY *= 0.85;

    // Add randomness for next frame
    if (screenShake.intensity > 0.5) {
      screenShake.offsetX = (Math.random() - 0.5) * screenShake.intensity;
      screenShake.offsetY = (Math.random() - 0.5) * screenShake.intensity;
    }
  }
}

// ==============================================
// DAMAGE NUMBERS DISPLAY SYSTEM - moved to src/effects/damage-numbers/
// ==============================================

// Effects systems moved to src/effects/

// Initialize effects systems
const damageNumberManager = new DamageNumberManager();

// Set up dependencies for extracted modules
setDamageNumberDependencies(canvas, worldToScreen);
setCoordinateValidatorDependencies(canvas, state, worldToScreen);

// Set up handler dependencies and register all message handlers
setHandlerDependencies({
  state,
  getPlayerName: () => authState.username || "",
  now: () => performance.now(),
  updateLobbyDisplay,
  updateButtons,
  resizeCanvas,
  render,
  updateSmoothedMetrics,
  damageNumberManager,
  isVisible,
  CoordinateValidator,
  reconcileWithServer,
  triggerScreenShake,
  triggerScreenFlash,
  unifiedPostProcess,
  gpuExplosionParticles,
  previousPelletsCollected: { value: previousPelletsCollected },
  previousHealth: { value: previousHealth },
  previousBoostAt: { value: previousBoostAt },
  topbarEl,
  gameStatsEl,
  perfStatsEl,
  joinGameBtnEl: null,
  loggedInPlayBtnEl: null,
  setGlobalState: gameActions.setGlobalState,
});

// Register all message handlers
registerAllMessageHandlers(registerMessageHandler);

async function logout() {
  try {
    await betterAuthSignOut();
  } catch (error) {
    console.error("Logout error:", error);
  }

  // Clear auth state
  authState.token = null;
  authState.username = null;
  authState.avatarUrl = "";
  authState.isGuest = false;
  authState.isPaid = false;
  authState.stats = {
    kills: 0,
    deaths: 0,
    pellets: 0,
    wins: 0,
    games: 0,
    playtime: 0,
  };

  localStorage.removeItem("username");
  localStorage.removeItem("isPaid");
  localStorage.removeItem("playerName");
  localStorage.removeItem("authToken");
  localStorage.removeItem("stdb_auth_token");

  // Create new anonymous user
  await initializeAuth();

  // Reconnect realtime backend with the updated identity.
  console.log("Reconnecting realtime backend with new anonymous user...");
  try {
    await realtimeBackend.disconnect();
    await realtimeBackend.connect({
      token: authState.token ?? undefined,
    });
    setupPartyOnConnection();
    requestLobbyList();
    console.log("Realtime backend reconnected with new username");
  } catch (error) {
    console.error("Failed to reconnect realtime backend:", error);
  }
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
}

function updateAccountDisplay() {
  // Query elements dynamically since React portal renders them after initial load
  const guestAuthFormEl = $("guestAuthForm");
  const guestCallsignEl = $("guestCallsign");
  const loggedInEl = $("loggedIn");
  const accountUsernameEl = $("accountUsername");
  const accountAvatarEl = $("accountAvatar") as HTMLImageElement;
  const accountKillsEl = $("accountKills");
  const accountDeathsEl = $("accountDeaths");
  const accountPelletsEl = $("accountPellets");
  const accountWinsEl = $("accountWins");
  const accountGamesEl = $("accountGames");
  const upgradeBtnEl = $("upgradeBtn");

  if (authState.token && authState.username) {
    if (authState.isGuest) {
      // Anonymous user - show guest UI
      if (guestAuthFormEl) guestAuthFormEl.style.display = "block";
      if (loggedInEl) loggedInEl.style.display = "none";
      if (guestCallsignEl) {
        guestCallsignEl.textContent = authState.username;
      }
    } else {
      // Discord user - show account UI
      if (guestAuthFormEl) guestAuthFormEl.style.display = "none";
      if (loggedInEl) loggedInEl.style.display = "block";
      if (accountUsernameEl) {
        accountUsernameEl.textContent = authState.username;
      }
      if (accountAvatarEl && authState.avatarUrl) {
        accountAvatarEl.src = authState.avatarUrl;
        accountAvatarEl.style.display = "block";
      }
      if (accountKillsEl) {
        accountKillsEl.textContent = formatNumber(authState.stats.kills);
      }
      if (accountDeathsEl) {
        accountDeathsEl.textContent = formatNumber(authState.stats.deaths);
      }
      if (accountPelletsEl) {
        accountPelletsEl.textContent = formatNumber(authState.stats.pellets);
      }
      if (accountWinsEl) {
        accountWinsEl.textContent = formatNumber(authState.stats.wins);
      }
      if (accountGamesEl) {
        accountGamesEl.textContent = formatNumber(authState.stats.games);
      }
      if (upgradeBtnEl) {
        upgradeBtnEl.style.display = authState.isPaid ? "none" : "inline-block";
      }
    }
  } else {
    if (guestAuthFormEl) guestAuthFormEl.style.display = "block";
    if (loggedInEl) loggedInEl.style.display = "none";
  }
}

async function initializeAuth() {
  // better-auth removed — SpacetimeDB handles auth via identity
  // Restore username from localStorage if available
  const savedUsername = localStorage.getItem("username");
  if (savedUsername) {
    authState.username = savedUsername;
  }
  updateAccountDisplay();
}

// Use event delegation for Discord login button (element rendered by React portal)
document.addEventListener("click", async (e) => {
  const target = e.target as HTMLElement;
  if (target.id === "discordLoginBtn" || target.closest("#discordLoginBtn")) {
    const discordLoginBtnEl = $("discordLoginBtn") as HTMLButtonElement;
    if (!discordLoginBtnEl) return;

    try {
      discordLoginBtnEl.disabled = true;
      discordLoginBtnEl.textContent = "Signing in...";

      await signInWithDiscord();
    } catch (error) {
      console.error("Discord sign-in failed:", error);

      discordLoginBtnEl.disabled = false;
      discordLoginBtnEl.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M13.545 2.907a13.227 13.227 0 0 0-3.257-1.011.05.05 0 0 0-.052.025c-.141.25-.297.577-.406.833a12.19 12.19 0 0 0-3.658 0 8.258 8.258 0 0 0-.412-.833.051.051 0 0 0-.052-.025c-1.125.194-2.22.534-3.257 1.011a.041.041 0 0 0-.021.018C.356 6.024-.213 9.047.066 12.032c.001.014.01.028.021.037a13.276 13.276 0 0 0 3.995 2.02.05.05 0 0 0 .056-.019c.308-.42.582-.863.818-1.329a.05.05 0 0 0-.01-.059.051.051 0 0 0-.018-.011 8.875 8.875 0 0 1-1.248-.595.05.05 0 0 1-.02-.066.051.051 0 0 1 .015-.019c.084-.063.168-.129.248-.195a.05.05 0 0 1 .051-.007c2.619 1.196 5.454 1.196 8.041 0a.052.052 0 0 1 .053.007c.08.066.164.132.248.195a.051.051 0 0 1-.004.085 8.254 8.254 0 0 1-1.249.594.05.05 0 0 0-.03.03.052.052 0 0 0 .003.041c.24.465.515.909.817 1.329a.05.05 0 0 0 .056.019 13.235 13.235 0 0 0 4.001-2.02.049.049 0 0 0 .021-.037c.334-3.451-.559-6.449-2.366-9.106a.034.034 0 0 0-.02-.019Zm-8.198 7.307c-.789 0-1.438-.724-1.438-1.612 0-.889.637-1.613 1.438-1.613.807 0 1.45.730 1.438 1.613 0 .888-.637 1.612-1.438 1.612Zm5.316 0c-.788 0-1.438-.724-1.438-1.612 0-.889.637-1.613 1.438-1.613.807 0 1.451.730 1.438 1.613 0 .888-.631 1.612-1.438 1.612Z"/>
        </svg>
        Sign in with Discord
      `;
    }
  }
});

// Logout button - use event delegation
document.addEventListener("click", async (e) => {
  const target = e.target as HTMLElement;
  if (target.id === "logoutBtn" || target.closest("#logoutBtn")) {
    await logout();
  }
});

// Upgrade button - use event delegation
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (target.id === "upgradeBtn" || target.closest("#upgradeBtn")) {
    // Redirect to Polar checkout with account_id
    if (authState.username) {
      const checkoutUrl = `https://buy.polar.sh/polar_cl_9034bzbralHG7hw6UctgRFdNa7Ra7tGJtj7Y70P0Ipg?account_id=${encodeURIComponent(authState.username)}`;
      window.open(checkoutUrl, "_blank");
    }
  }
});

// Join game button - use event delegation
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (target.id === "joinGameBtn" || target.closest("#joinGameBtn")) {
    handleQuickJoin();
  }
});

// Logged in play button - use event delegation
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (target.id === "loggedInPlayBtn" || target.closest("#loggedInPlayBtn")) {
    handleQuickJoin();
  }
});

updateAccountDisplay();

state.stars = generateStars({ width: canvas.width, height: canvas.height });

setModalDependencies({
  resetAutoJoinAttempt: () => {
    /* Handled in browser module */
  },
  startLobbyBrowser: () => {
    startLobbySimulationUpdates(
      () => {},
      () => [],
    );
  },
  stopLobbyBrowser: () => {
    stopLobbySimulationUpdates();
  },
  joinSpecificLobby,
  requestLobbyList,
});

initializeModals();

// Initialize input system with new provider architecture
const deviceType = detectDeviceType();
console.log(
  `[InputManager] ${deviceType === "mobile" ? "Mobile" : "Desktop"} device detected, using ${deviceType === "mobile" ? "TouchInput" : "KeyboardMouseInput"}`,
);
const inputProvider: InputProvider = createInputProvider(deviceType, canvas);
setInputProvider(inputProvider);

// Start input transmission at server tick rate
startInputTransmission();

// Strafe lock (Shift): lock thrust angle, rotation continues
function handleGlobalKeyDown(e: KeyboardEvent) {
  // Debug combo: Ctrl+Shift+P (or Cmd+Shift+P on Mac) to give all players pellets
  if (e.key === "P" && (e.ctrlKey || e.metaKey) && e.shiftKey) {
    if (isActiveRealtimeBackendConnected()) {
      dispatchNetworkMessage({ t: "debug_pellet_boost" });
    }
    e.preventDefault();
    return;
  }

  // Debug combo: Ctrl+Shift+I (or Cmd+Shift+I on Mac) to toggle invincibility
  if (e.key === "I" && (e.ctrlKey || e.metaKey) && e.shiftKey) {
    if (isActiveRealtimeBackendConnected()) {
      dispatchNetworkMessage({ t: "debug_toggle_invincibility" });
    }
    e.preventDefault();
    return;
  }

  // Debug combo: Ctrl+Shift+D (or Cmd+Shift+D on Mac) for instant death (E2E tests)
  if (e.key === "D" && (e.ctrlKey || e.metaKey) && e.shiftKey) {
    if (isActiveRealtimeBackendConnected()) {
      dispatchNetworkMessage({ t: "debug_instant_death" });
    }
    e.preventDefault();
    return;
  }

  // Debug combo: Ctrl+Shift+V (or Cmd+Shift+V on Mac) for instant victory (E2E tests)
  if (e.key === "V" && (e.ctrlKey || e.metaKey) && e.shiftKey) {
    if (isActiveRealtimeBackendConnected()) {
      dispatchNetworkMessage({ t: "debug_instant_victory" });
    }
    e.preventDefault();
    return;
  }

  if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    const angle =
      state.predictedPlayer?.rotation ?? state.serverPlayer?.rotation;
    if (typeof angle === "number") {
      setLockedThrustAngle(angle);
    }
  }
}

function handleGlobalKeyUp(e: KeyboardEvent) {
  if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    setLockedThrustAngle(undefined);
  }
}

window.addEventListener("keydown", handleGlobalKeyDown);
window.addEventListener("keyup", handleGlobalKeyUp);

// Auth initialization moved to initializeApp() at top of file

// ============================================================================
// REACT UI INTEGRATION
// ============================================================================

// Initialize and start the client game orchestrator
// Set up the update functions for the orchestrator
clientGameOrchestrator.initialize(
  {
    interpolatePlayers,
    interpolateAsteroids,
    interpolatePellets,
    updateBulletPositions,
    updateCamera,
    updateMouseInput: () => updateInput(1 / 60), // Call with fixed timestep for input updates
    updateFPS,
    render,
    updateParticles: (deltaTime: number) => {
      damageNumberManager.updateAll(deltaTime);
      if (gpuExplosionParticles) {
        gpuExplosionParticles.update(deltaTime);
      }
    },
    updateThrustParticles: (_deltaTime: number) => {
      // Clean up old trails
      const currentTime = Date.now();
      for (const [playerId, trail] of thrustTrails) {
        if (currentTime - trail.lastUpdate > 5000) {
          thrustTrails.delete(playerId);
        }
      }
    },
    spawnThrustParticles: () => {
      if (state.started) {
        // Update trails for all players
        state.players.forEach((player) => {
          if (!player.pos) return;
          if (!thrustTrails.has(player.id)) {
            thrustTrails.set(player.id, createThrustTrail(player.id));
          }
          const trail = thrustTrails.get(player.id)!;

          updateThrustTrail(
            trail,
            player.pos.x,
            player.pos.y,
            player.rotation,
            true, // Always show trails
            player.scale || 1.0,
          );
        });
      }
    },
  },
  () => state,
);

// Start the client game loop
clientGameOrchestrator.start();

// Initialize audio system with pre-loaded files
initializeAudioSystem();

// Initialize React UI integration
initializeReactIntegration();

// Initialize connection status UI component
initializeConnectionStatus();

// Initialize splash text on login screen
initializeSplashText();

// Initialize performance stats UI component
initializePerformanceStats();

// Initialize countdown UI component
initializeCountdown();

// Initialize bug report UI component
initializeBugReportUI();

// Setup error capture for bug reports
setupErrorCapture();

// Setup bug report dependencies
setBugReportDependencies({
  getGameState: () => ({
    lobbyId: state.lobbyId,
    players: state.players,
    started: state.started,
  }),
  getPlayerState: () => state.serverPlayer,
});

// Setup bug report button event listener
if (bugReportBtnEl) {
  bugReportBtnEl.addEventListener("click", () => {
    showBugReportModal();
  });
}
